import type { LegacyResult, Results } from '../../../../types/preloan';
import type { PreApproveType } from '../../../../types/preloan/v3';

// Textos configurados por el partner que pueden venir en HTML (<p>…</p>): como texto plano
// donde va con el estilo del rediseño (títulos, líneas de contacto).
export const htmlToText = (html: string) => (
  new DOMParser().parseFromString(html, 'text/html').body.textContent || ''
).trim();

// ---------- Pre-aprobación (4f) ----------

const acceptedFirst = (preApproves: PreApproveType[]) => {
  const accepted = preApproves.filter((pa) => pa.accepted);
  return accepted.length > 0 ? accepted : preApproves;
};

// Misma prioridad que la pantalla actual: primero las propias (no transferibles),
// luego la principal (productSubtype "main") y luego el mayor monto.
export const pickMainMortgage = (preApproves: PreApproveType[] = []) => {
  const mortgages = acceptedFirst(preApproves.filter((pa) => pa.productType === 'mortgage'));
  if (mortgages.length === 0) return undefined;
  const own = mortgages.filter((pa) => !pa.isTransferrable);
  const pool = own.length > 0 ? own : mortgages;
  return [...pool].sort((a, b) => {
    if (a.productSubtype === 'main' && b.productSubtype !== 'main') return -1;
    if (b.productSubtype === 'main' && a.productSubtype !== 'main') return 1;
    return b.maxAmount - a.maxAmount;
  })[0];
};

// Las mismas que mostraba el carrusel del flujo anterior (firstMessageSwitch): las propias
// (no transferibles) o, si no hay, las transferibles; todos los productos. La hipotecaria
// principal va primero (tarjeta grande) y el resto en el orden en que vienen.
export const pickShownPreApproves = (preApproves: PreApproveType[] = []) => {
  const own = preApproves.filter((pa) => !pa.isTransferrable);
  const transferable = preApproves.filter((pa) => pa.isTransferrable);
  // Ya transferido (Max, 2 nov): solo las de la mutuaria a la que se envió; las otras ya no
  // aplican. alreadyTransferred viene solo en las del destino real (fix del backend).
  const sent = transferable.filter((pa) => pa.alreadyTransferred);
  let shown = own;
  if (own.length === 0) shown = sent.length > 0 ? sent : transferable;
  const main = pickMainMortgage(shown);
  return main ? [main, ...shown.filter((pa) => pa !== main)] : shown;
};

// La solicitud ya se envió a una mutuaria: no se ofrece complementar la renta.
export const isAlreadyTransferred = (preApproves: PreApproveType[] = []) => preApproves
  .some((pa) => pa.isTransferrable && pa.alreadyTransferred);

// Dividendo estimado en UF, sin seguros: cuota francesa con tasa anual / 12.
export const estimatedDividendUf = (pa: PreApproveType) => {
  const r = pa.rate / 12;
  const n = pa.maxPeriods;
  if (!n || !pa.maxAmount) return undefined;
  if (!r) return pa.maxAmount / n;
  return (pa.maxAmount * r) / (1 - (1 + r) ** -n);
};

const es = (value: number, digits = 0) => value.toLocaleString('es-CL', {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits,
});

// El front actual muestra el monto hipotecario como UF (PreApproveCard).
export const formatUf = (value: number, digits = 0) => `UF ${es(value, digits)}`;
export const formatClp = (value: number) => `$${es(Math.round(value))}`;
export const formatRate = (rate: number) => `${es(rate * 100, 2)}%`;
export const formatYears = (months: number) => {
  const years = months / 12;
  return `${Number.isInteger(years) ? years : es(years, 1)} años`;
};

export const formatPreApproveAmount = (pa: PreApproveType) => (
  pa.productType === 'mortgage' || pa.currency?.abbreviation === 'CLF'
    ? formatUf(pa.maxAmount)
    : formatClp(pa.maxAmount)
);

// ---------- Motivos de rechazo (4fc) ----------

export type ReasonCategory = 'debts' | 'punished' | 'employment' | 'gaps'
| 'age' | 'score' | 'amount' | 'income';

type CategoryCopy = { title: string, text: string };

// alimony_debtor (pensión de alimentos) ya no se muestra como motivo: si es el único filtro
// fallido se cae al motivo genérico.
const CATEGORY_FILTERS: Record<ReasonCategory, string[]> = {
  debts: ['protest_and_delinquency_1year', 'protest_and_delinquency_5years'],
  punished: ['punished_debt', 'had_punished_loan'],
  employment: ['min_emp_length', 'emp_length', 'type_of_contract'],
  gaps: ['lagunes'],
  age: ['min_age', 'max_age', 'age'],
  score: ['external_score', 'risk_models', 'w2p', 'max_licenses', 'licenses'],
  amount: ['min_loan_policy_amount', 'product_availability', 'pre_required_associated_product_approved'],
  income: [
    'min_net_salary', 'min_brute_salary', 'max_financial_stress', 'max_mortgage_financial_stress',
    'leverage_total_debt', 'leverage_consumer_debt', 'max_dpp_financial_stress', 'consumer_debt',
    'dpp_financial_stress',
  ],
};

// Orden: primero lo que un complemento de renta no resuelve, al final renta/carga.
const CATEGORY_ORDER: ReasonCategory[] = [
  'debts', 'punished', 'employment', 'gaps', 'age', 'score', 'amount', 'income',
];

// [validar textos con Riesgo y Legal]
export const CATEGORY_COPY: Record<ReasonCategory, CategoryCopy> = {
  debts: {
    title: 'Protestos o morosidades',
    text: 'Tienes documentos protestados o deudas impagas registradas.',
  },
  punished: {
    title: 'Deuda castigada',
    text: 'Una institución dio por perdida una deuda a tu nombre.',
  },
  employment: {
    title: 'Antigüedad laboral',
    text: 'Todavía no cumples el tiempo mínimo en tu empleo actual.',
  },
  gaps: {
    title: 'Lagunas en tus cotizaciones',
    text: 'Hay meses sin cotizaciones previsionales en el período que revisamos.',
  },
  age: {
    title: 'Edad',
    text: 'Tu edad está fuera del rango que pide este crédito.',
  },
  score: {
    title: 'Puntaje de crédito',
    text: 'Tu puntaje de crédito está bajo el mínimo para este producto.',
  },
  amount: {
    title: 'Monto mínimo',
    text: 'El monto que podrías financiar no alcanza el mínimo de este producto.',
  },
  income: {
    title: 'Renta o carga financiera',
    text: 'Tus ingresos o tu nivel de deuda no alcanzan lo que pide este crédito.',
  },
};

const categoryOf = (filter: string): ReasonCategory | undefined => CATEGORY_ORDER
  .find((category) => CATEGORY_FILTERS[category].includes(filter));

const failed = (value: unknown) => value === false || value === 'R' || value === 'G';

export const rejectionCategories = (results?: (Results | LegacyResult)[]) => {
  const found = new Set<ReasonCategory>();
  (results || []).forEach((result) => {
    Object.entries(result.hardFilterResults || {}).forEach(([filter, value]) => {
      if (!failed(value)) return;
      const category = categoryOf(filter);
      if (category) found.add(category);
    });
  });
  return CATEGORY_ORDER.filter((category) => found.has(category));
};
